<template>
    <div class="home">
        <!-- Hero Section -->
        <section class="hero">
            <div class="container">
                <h1>Global Business News</h1>
                <p>China | Southeast Asia Zone</p>
            </div>
        </section>

        <section class="analysis-recommendations-new">
            <div class="container">
                <!-- Section Title -->
                <div class="section-title">
                    <h2>Analysis and Recommendations</h2>
                </div>

                <!-- Search Mode Toggle Buttons -->
                <div class="search-mode-toggle">
                    <button class="mode-btn" :class="{ active: currentMode === 'industry' }"
                        @click="switchMode('industry')">
                        Search by Industry
                    </button>
                    <button class="mode-btn" :class="{ active: currentMode === 'country' }"
                        @click="switchMode('country')">
                        Search by Country
                    </button>
                </div>

                <!-- Industry Search Mode -->
                <div v-if="currentMode === 'industry'" class="search-mode-content">
                    <!-- Search Box -->
                    <div class="search-box">
                        <div class="search-input-wrapper">
                            <img src="../assets/search_icon.png" alt="Search" class="search-icon" />
                            <input type="text" v-model="industrySearchQuery"
                                placeholder="Quick search, enter the industry you wish to view"
                                @keyup.enter="searchIndustries" class="search-input" />
                        </div>
                    </div>

                    <!-- Industries Grid -->
                    <div class="industries-grid" :class="{
                             'result-count-1': filteredIndustries.length === 1,
                             'result-count-2': filteredIndustries.length === 2,
                             'result-count-3': filteredIndustries.length === 3
                         }" v-if="filteredIndustries.length > 0">
                        <div class="industry-item" v-for="industry in filteredIndustries" :key="industry.industry"
                            :class="{ active: selectedIndustry === industry.industry }" 
                            @click="selectIndustry(industry.industry)">
                            {{ formatIndustryName(industry.industry) }}
                        </div>
                    </div>

                    <!-- Search Button -->
                    <div class="search-action">
                        <button class="search-btn" @click="searchByIndustry">
                            Search Countries
                        </button>
                    </div>

                    <!-- Recommended Countries -->
                    <div v-if="showRecommendedCountries" class="recommended-countries">
                        <div class="recommendation-text">
                            Based on a comprehensive comparative analysis of platform data and multiple authoritative
                            sources,
                            the most suitable countries for the global expansion of
                            <strong>{{ getSelectedIndustryName() }}</strong> are as follows:
                        </div>

                        <!-- Countries Grid -->
                        <div class="countries-grid" :class="{
                                 'result-count-1': recommendedCountries.length === 1,
                                 'result-count-2': recommendedCountries.length === 2,
                                 'result-count-3': recommendedCountries.length === 3
                             }">
                            <div class="country-item" v-for="country in recommendedCountries" :key="country"
                                :class="{ active: selectedCountry === country }" @click="selectCountry(country)">
                                {{ country }}
                            </div>
                        </div>

                        <!-- View Details Button -->
                        <div class="view-details-btn">
                            <button class="details-btn" @click="viewCountryDetails">
                                Search Cities‌
                            </button>
                        </div>
                    </div>

                    <!-- Recommended Cities -->
                    <div v-if="showRecommendedCities" class="recommended-countries">
                        <div class="recommendation-text">
                            Based on a comprehensive comparative analysis of platform data and multiple authoritative
                            sources,
                            the most suitable cities for the global expansion of
                            <strong>{{ getSelectedCountryName() }}</strong> are as follows:
                        </div>

                        <!-- Cities Grid -->
                        <div class="countries-grid" :class="{
                                    'result-count-1': recommendedCities.length === 1,
                                    'result-count-2': recommendedCities.length === 2,
                                    'result-count-3': recommendedCities.length === 3
                                }">
                            <div class="country-item" v-for="city in recommendedCities" :key="city"
                                :class="{ active: selectedCity === city }" @click="selectCity(city)">
                                {{ city }}
                            </div>
                        </div>

                        <!-- View City Info Button -->
                        <div class="view-details-btn">
                            <button class="details-btn" @click="viewCityInfo">
                                View City Info
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Country Search Mode -->
                <div v-if="currentMode === 'country'" class="search-mode-content">
                    <div class="file-library-title">
                        Please Select File Library
                    </div>

                    <!-- Country Search Form -->
                    <div class="country-search-form">
                        <div class="form-group">
                            <select v-model="selectedCountryForSearch" class="form-select" @change="onCountryChange">
                                <option value="">Select Country</option>
                                <option v-for="country in allCountries" :key="country.name" :value="country.name">
                                    {{ country.name_en }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <select v-model="selectedState" class="form-select" @change="onStateChange">
                                <option value="">Select State/Province</option>
                                <option v-for="state in filteredStates" :key="state.name" :value="state.name">
                                    {{ state.name_en }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <select v-model="selectedCity" class="form-select">
                                <option value="">Select City</option>
                                <option v-for="city in filteredCities" :key="city.name" :value="city.sign">
                                    {{ city.name_en }}
                                </option>
                            </select>
                        </div>
                        <div class="form-group">
                            <button class="search-btn" @click="searchByCountry">
                                Search >>
                            </button>
                        </div>
                    </div>

                    <!-- Country Documents Grid -->
                    <div v-if="showCountryDocuments" class="documents-grid">
                        <div class="document-card" v-for="doc in countryDocuments" :key="doc.sign"
                            @click="openDocument(doc)">
                            <div class="document-image">
                                <img :src="doc.icon" :alt="doc.name" @error="handleImageError">
                                <!-- 中间文字：绝对定位叠加 -->
                                <span class="image-text">{{ doc.name_en }}</span>
                            </div>
                            <div class="document-summary">
                                <p>{{ doc.describe }}</p>
                                <span class="document-date">{{ doc.lastUpdate }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
        
        <Predict ref="predict" />
        <ContactsUs ref="contactsUs" />

    </div>
</template>

<script>
    import Predict from '../components/Predict.vue';
    import ContactsUs from '../components/ContactsUs.vue';

    export default {
        name: 'News',
        components: {
            Predict,
            ContactsUs
        },
        data() {
            return {
                // 新添加的分析推荐部分数据
                currentMode: 'industry',
                industrySearchQuery: '',
                selectedIndustry: null,
                selectedCountry: null,

                // Industry Search Mode 数据
                industryData: [],
                recommendedCountries: [],
                recommendedCities: [],

                // Country Search Mode 数据
                countryProvincesData: [],
                cityResourcesData: [],
                selectedCountryForSearch: '',
                selectedState: '',
                selectedCity: '',
                allCountries: [],
                filteredStates: [],
                filteredCities: [],
                restoreSetting:false,
                countryDocuments: []
            }
        },
        watch: {
            // 监听过滤后的结果变化
            filteredIndustries(newFilteredList) {
                console.log("filteredIndustries changed:", newFilteredList);
                if(!this.restoreSetting){
                    //如果不是恢复数据，重置
                    console.log("watch reset");
                    this.recommendedCountries = [];
                    this.recommendedCities = [];
                    this.selectecCity = null;
                    this.selectedCountry = null;
                    this.selectedIndustry = null;
                }else{
                    //如果恢复数据，则不重置
                    console.log("watch not reset");
                    this.restoreSetting = false;
                }
            }
        },
        computed: {
            filteredIndustries() {
                if (!this.industrySearchQuery) {
                    return this.industryData;
                }
                return this.industryData.filter(industry => 
                    industry.industry.toLowerCase().includes(this.industrySearchQuery.toLowerCase())
                );
            },
            showRecommendedCountries() {
                return this.recommendedCountries.length > 0;
            },
            showRecommendedCities() {
                return this.recommendedCities.length > 0;
            },
            showCountryDocuments() {
                return this.countryDocuments.length > 0;
            }
        },
        async mounted() {
            // 恢复保存的状态
            this.restoreState();
            
            // await this.fetchPredictionData();
            if(!this.restoreSetting){
                await this.fetchIndustryData();
            }
            await this.fetchCountryProvincesData();
            // 默认选中第一个行业（如果没有恢复的状态）
            if (this.industryData.length > 0 && !this.selectedIndustry) {
                this.selectedIndustry = this.industryData[0].industry;
            }
        },
        beforeUnmount() {
            // 保存状态到sessionStorage
            this.saveState();
        },
        methods: {
            async fetchIndustryData() {
                try {
                    const response = await fetch('/res/config/os/industry.json');
                    this.industryData = await response.json();
                } catch (error) {
                    console.error('Failed to fetch industry data:', error);
                    // 备用数据
                    this.industryData = [
                        {
                            "industry": "New Energy / 新能源",
                            "countries": [
                                {
                                    "country": "China / 中国",
                                    "cities": [
                                        {
                                            "city": "Shenzhen / 深圳",
                                            "sign": "Shenzhen"
                                        },
                                        {
                                            "city": "Hefei / 合肥",
                                            "sign": "Hefei"
                                        }
                                    ]
                                }
                            ]
                        }
                    ];
                }
            },

            async fetchCountryProvincesData() {
                try {
                    const response = await fetch('/res/config/os/country_provinces_city.json');
                    this.countryProvincesData = await response.json();
                    this.extractCountries();
                    
                    // 数据加载完成后，更新过滤后的状态和城市列表
                    this.updateFilteredStates();
                    this.updateFilteredCities();
                } catch (error) {
                    console.error('Failed to fetch country provinces data:', error);
                    // 备用数据
                    this.countryProvincesData = [
                        {
                            "name": "中国",
                            "name_en": "China",
                            "provinces": [
                                {
                                    "name": "海南省",
                                    "name_en": "Hainan",
                                    "cities": [
                                        {
                                            "name": "海口市",
                                            "name_en": "Haikou",
                                            "sign": "Haikou"
                                        },
                                        {
                                            "name": "三亚市",
                                            "name_en": "Sanya",
                                            "sign": "Sanya"
                                        }
                                    ]
                                }
                            ]
                        }
                    ];
                    this.extractCountries();
                    
                    // 数据加载完成后，更新过滤后的状态和城市列表
                    this.updateFilteredStates();
                    this.updateFilteredCities();
                }
            },

            async fetchCityResourcesData() {
                try {
                    const response = await fetch('/res/config/os/city_resources.json');
                    this.cityResourcesData = await response.json();
                } catch (error) {
                    console.error('Failed to fetch city resources data:', error);
                    // 备用数据
                    this.cityResourcesData = [
                        {
                            "sign": "Beijing",
                            "name_en": "Beijing",
                            "name": "北京",
                            "describe": "Beijing Investment Guide",
                            "city_describe": "",
                            "icon": "https://res.roadlinkglb.com/pic/os/zstz.jpeg"
                        },
                        {
                            "sign": "Shanghai",
                            "name_en": "Shanghai",
                            "name": "上海",
                            "describe": "Shanghai Investment Guide",
                            "city_describe": "",
                            "icon": "https://res.roadlinkglb.com/pic/os/zstz.jpeg"
                        }
                    ];
                }
            },

            extractCountries() {
                this.allCountries = this.countryProvincesData;
            },

            formatIndustryName(industry) {
                return industry.split(' / ')[0];
            },

            scrollToContact() {
                document.querySelector('.contact').scrollIntoView({
                    behavior: 'smooth'
                })
            },

            openDocument(doc) {
                // 保存状态后再跳转
                this.saveState();
                // 根据选中的城市进行跳转页面。
                this.$router.push('/city-info/' + doc.sign);
                // window.open(doc.file, '_blank')
            },

            handleImageError(event) {
                // 图片加载失败时使用默认图片
                event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
            },

            switchMode(mode) {
                this.currentMode = mode;
                this.resetIndustrySearch();
                this.resetCountrySearch();
            },

            selectIndustry(industry) {
                this.selectedIndustry = industry;
                this.recommendedCountries = [];
                this.selectedCountry = null;
                this.recommendedCities = [];
                this.selectedCity = null;
            },

            selectCountry(country) {
                this.selectedCountry = country;
                this.recommendedCities = [];
                this.selectedCity = null;
            },

            selectCity(city) {
                this.selectedCity = city;
            },

            searchIndustries() {
                // 搜索逻辑已经在computed属性filteredIndustries中处理
                // 这里只需要确保有选中项
                if (this.filteredIndustries.length > 0 && !this.selectedIndustry) {
                    this.selectedIndustry = this.filteredIndustries[0].industry;
                }
            },

            searchByIndustry() {
                if (this.selectedIndustry) {
                    const selectedIndustryData = this.industryData.find(item => 
                        item.industry === this.selectedIndustry
                    );

                    if (selectedIndustryData && selectedIndustryData.countries) {
                        // 直接从industryData中提取国家列表
                        this.recommendedCountries = selectedIndustryData.countries.map(country => country.country);
                    } else {
                        // 如果没有找到匹配的数据，显示默认国家
                        this.recommendedCountries = ['China / 中国'];
                    }
                    
                    this.selectedCountry = this.recommendedCountries[0] || null;
                } else {
                    this.$toast.show({
                        // title: 'Prompt message',
                        message: 'Please select an industry',
                        // type: 'warning',
                        duration: 0
                    });
                    // alert('Please select an industry');
                }
            },

            // 查看城市信息
            viewCityInfo() {
                // 保存状态后再跳转
                this.saveState();
                const sign = this.industryData
                    .find(item => item.industry === this.selectedIndustry).countries
                    .find(item => item.country === this.selectedCountry).cities
                    .find(city => city.city === this.selectedCity).sign;
                this.$router.push('/city-info/' + sign);
            },

            viewCountryDetails() {
                if (this.selectedCountry) {
                    this.currentReport = {
                        title: `${this.selectedCountry} - ${this.getSelectedIndustryName()} Industry Analysis`,
                        content: `Detailed analysis for ${this.getSelectedIndustryName()} industry in ${this.selectedCountry}. This includes market size, growth potential, regulatory environment, and key opportunities.`
                    };
                    this.showReportModal = true;
                }
                // 根据选中的国家获取城市列表
                if (this.selectedCountry) {
                    this.recommendedCities = this.industryData
                        .find(item => item.industry === this.selectedIndustry).countries
                        .find(item => item.country === this.selectedCountry).cities.map(city => city.city);
                    if (this.recommendedCities.length > 0) {
                        this.selectedCity = this.recommendedCities[0];
                    } else {
                        this.$toast.show({
                            // title: 'Prompt message',
                            message: 'No cities found for the selected country',
                            // type: 'warning',
                            duration: 0
                        });
                    }
                } else {
                    this.$toast.show({
                        // title: 'Prompt message',
                        message: 'Please select an country',
                        // type: 'warning',
                        duration: 0
                    });
                }

            },

            getSelectedIndustryName() {
                if (!this.selectedIndustry) return '';
                return this.formatIndustryName(this.selectedIndustry);
            },

            getSelectedCountryName() {
                if (!this.selectedCountry) return '';
                return this.selectedCountry;
            },

            resetIndustrySearch() {
                this.industrySearchQuery = '';
                this.selectedIndustry = this.industryData[0]?.industry || null;
                this.recommendedCountries = [];
                this.recommendedCities = [];
                this.selectedCountry = null;
            },

            onCountryChange() {
                this.selectedState = '';
                this.selectedCity = '';
                this.updateFilteredStates();
            },

            onStateChange() {
                this.selectedCity = '';
                this.updateFilteredCities();
            },

            updateFilteredStates() {
                const selectedCountry = this.allCountries.find(c => c.name === this.selectedCountryForSearch);
                this.filteredStates = selectedCountry ? selectedCountry.provinces : [];
            },

            updateFilteredCities() {
                const selectedCountry = this.allCountries.find(c => c.name === this.selectedCountryForSearch);
                if (selectedCountry) {
                    const selectedProvince = selectedCountry.provinces.find(p => p.name === this.selectedState);
                    this.filteredCities = selectedProvince ? selectedProvince.cities : [];
                } else {
                    this.filteredCities = [];
                }
            },

            async searchByCountry() {
                if (!this.selectedCountryForSearch) {
                    // alert('Please select a country');
                    this.$toast.show({
                        // title: 'Prompt message',
                        message: 'Please select a country',
                        // type: 'warning',
                        duration: 0
                    });
                    return;
                }

                if (!this.selectedState) {
                    // alert('Please select a country');
                    this.$toast.show({
                        // title: 'Prompt message',
                        message: 'Please select a state/province',
                        // type: 'warning',
                        duration: 0
                    });
                    return;
                }

                if (!this.selectedCity) {
                    // alert('Please select a country');
                    this.$toast.show({
                        // title: 'Prompt message',
                        message: 'Please select a city',
                        // type: 'warning',
                        duration: 0
                    });
                    return;
                }


                await this.fetchCityResourcesData();

                let documents = [];
                
                // 根据选择的城市sign来匹配文档
                if (this.selectedCity) {
                    // 如果选择了具体城市
                    const cityDoc = this.cityResourcesData.find(city => city.sign === this.selectedCity);
                    if (cityDoc) {
                        documents = [cityDoc];
                    }
                } else if (this.selectedState) {
                    // 如果只选择了省份，显示该省份下所有城市的文档
                    const selectedCountry = this.allCountries.find(c => c.name === this.selectedCountryForSearch);
                    if (selectedCountry) {
                        const selectedProvince = selectedCountry.provinces.find(p => p.name === this.selectedState);
                        if (selectedProvince) {
                            documents = selectedProvince.cities.map(city => {
                                return this.cityResourcesData.find(doc => doc.sign === city.sign);
                            }).filter(doc => doc); // 过滤掉undefined
                        }
                    }
                } else {
                    // 如果只选择了国家，显示国家下的所有文档
                    documents = this.cityResourcesData;
                }

                this.countryDocuments = documents;
            },

            resetCountrySearch() {
                this.selectedCountryForSearch = '';
                this.selectedState = '';
                this.selectedCity = '';
                this.filteredStates = [];
                this.filteredCities = [];
                this.countryDocuments = [];
            },

            // 保存状态到sessionStorage
            saveState() {
                const state = {
                    currentMode: this.currentMode,
                    industryData: this.industryData,
                    industrySearchQuery: this.industrySearchQuery,
                    selectedIndustry: this.selectedIndustry,
                    selectedCountry: this.selectedCountry,
                    selectedCity: this.selectedCity,
                    recommendedCountries: this.recommendedCountries,
                    recommendedCities: this.recommendedCities,
                    selectedCountryForSearch: this.selectedCountryForSearch,
                    selectedState: this.selectedState,
                    countryDocuments: this.countryDocuments,
                    showRecommendedCountries:this.showRecommendedCountries,
                    showRecommendedCities:this.showRecommendedCities,
                };
                sessionStorage.setItem('newsPageState', JSON.stringify(state));
            },

            // 从sessionStorage恢复状态
            restoreState() {
                const savedState = sessionStorage.getItem('newsPageState');
                if (savedState) {
                    this.restoreSetting = true;
                    const state = JSON.parse(savedState);
                    Object.assign(this, state);
                }
            },
        }
    }
</script>

<style scoped>
    /* 基础样式 */
    .container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 20px;
        box-sizing: border-box;
    }

    /* Hero Section */
    .hero {
        background: url('../assets/home_banner.jpg');
        background-size: cover;
        background-position: center;
        color: white;
        padding: 100px 0;
        text-align: center;
    }

    .hero h1 {
        font-size: 36px;
        margin-bottom: 20px;
        line-height: 1.2;
    }

    .hero p {
        font-size: 16px;
        max-width: 700px;
        margin: 0 auto 27px;
        line-height: 1.5;
    }

    /* Diagnosis Section */
    .diagnosis {
        padding: 100px 0;
        background-color: #2444CB;
    }

    .diagnosis .section-title h2 {
        font-size: 44px;
        color: white;
    }

    .diagnosis .section-title p {
        font-size: 16px;
        color: white;
    }

    .title-decoration {
        height: 12px;
        width: 60px;
        background-color: #FF9E1B;
        margin-bottom: 16px;
    }

    .diagnosis-container {
        margin: 0 auto;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    }

    .diagnosis-form {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 20px;
    }

    /* 下拉框样式调整 */
    .form-group {
        position: relative;
    }

    .form-group select {
        width: 100%;
        font-size: 16px;
        padding: 12px 16px;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-sizing: border-box;
        /* 移除默认箭头并添加自定义样式 */
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: calc(100% - 12px) center;
        /* 向左移动20px */
        background-size: 16px;
        padding-right: 40px;
        /* 增加右侧内边距为箭头留空间 */
    }

    /* 单独设置联系表单的placeholder颜色 */
    .contact-form input::placeholder,
    .contact-form textarea::placeholder,
    .contact-form select:invalid {
        color: black;
    }

    .contact-form select option[value=""] {
        color: black;
    }

    .form-buttons {
        grid-column: 1 / -1;
        display: flex;
        gap: 15px;
        margin-top: 20px;
    }

    .btn-primary {
        background-color: #FF9E1B;
        color: black;
        border: none;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        flex: 1;
    }

    .btn-secondary {
        background-color: transparent;
        color: white;
        border: 1px solid white;
        padding: 12px 24px;
        border-radius: 4px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        flex: 1;
    }

    /* Analysis Section */
    .analysis-recommend {
        padding: 60px 0;
        background-color: white;
    }

    .analysis-recommend h3 {
        font-size: 32px;
        color: #222222;
        margin-bottom: 30px;
    }

    .analysis-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 30px;
        margin-top: 44px;
    }

    .analysis-item {
        border: 1px solid #2444CB;
        border-radius: 8px;
        padding: 20px 0;
        background-color: white;
        display: flex;
        flex-direction: column;
    }

    .analysis-header {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        height: 40px;
        padding: 0 20px;
    }

    .analysis-header img {
        height: 23px;
        width: 23px;
    }

    .analysis-header p {
        margin-left: 10px;
        font-family: Arial, Arial;
        font-weight: normal;
        font-size: 18px;
        color: #222222;
        line-height: 16px;
        font-style: normal;
        text-transform: none;
    }

    .divider {
        height: 1px;
        background-color: #2444CB;
        width: 100%;
        margin: 10px 0;
    }

    .analysis-content {
        display: flex;
        flex-direction: column;
        padding: 25px;
        /* 增加内边距，使文字与边框距离增加 */
        font-size: 16px;
        flex: 1;
    }

    .content-label {
        color: #303030;
        margin-bottom: 12px;
        /* 增加标签与内容框之间的距离 */
    }

    .content-box {
        height: 50px;
        display: flex;
        align-items: center;
        margin-bottom: 18px;
        /* 增加内容框之间的间距 */
    }

    .empty-text {
        color: #999999;
    }

    .filled-box {
        border: 2px solid #2444CB;
        border-radius: 6px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        font-size: 14px;
        padding: 0 10px;
        /* 为填充框内的文字增加内边距 */
    }

    .filled-box p {
        color: #2444CB;
        margin: 0;
    }

    .contact-link {
        display: flex;
        align-items: left;
        font-size: 14px;
        margin-top: 15px;
        /* 增加联系链接的顶部间距 */
    }

    .contact-link a {
        color: #265FFF;
        text-decoration: none;
    }

    .report-buttons {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 15px;
        margin-top: 30px;
        padding: 0 20px;
    }

    .btn-outline {
        background-color: white;
        border: 1px solid #265FFF;
        color: #265FFF;
        padding: 12px 24px;
        border-radius: 4px;
        font-family: PingFang-SC, PingFang-SC;
        font-weight: 800;
        font-size: 14px;
        font-style: normal;
        height: 40px;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    /* Related Services */
    .related-services {
        padding: 60px 0;
        background-color: #2444CB;
        width: 100%;
    }

    .related-services .section-title h2 {
        font-size: 24px;
        color: white;
        font-family: PingFangSC, PingFang SC;
        font-weight: 600;
    }

    .services-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
        gap: 30px;
        width: 100%;
    }

    .service-card {
        background-color: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    }

    .card-header {
        background-color: #265FFF;
        color: white;
        padding-left: 31px;
    }

    .card-header h3 {
        margin: 0;
        font-size: 24px;
        font-family: Arial, Arial;
        font-weight: normal;
        height: 60px;
        display: flex;
        align-items: center;
    }

    .card-content {
        padding: 20px 25px 0 31px;
    }

    .card-content ul {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    .card-content li {
        margin-bottom: 12px;
        padding-left: 20px;
        position: relative;
        font-size: 16px;
        line-height: 1.4;
    }

    .card-content li:before {
        content: "•";
        color: #265FFF;
        font-weight: bold;
        position: absolute;
        left: 0;
    }

    /* Featured Documents - 修改为三等分 */
    .documents-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 30px;
        width: 100%;
    }

    .document-card {
        border-radius: 8px;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .document-card:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }

    .document-image {
        height: 200px;
        overflow: hidden;
        transform-style: preserve-3d; /* 新增：关键属性 */
        flex-shrink: 0;
    }

    .document-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
    }

    .document-card:hover .document-image img {
        transform: scale(1.05);
    }

    .document-summary {
        padding: 20px;
        background-color: white;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
    }

    .document-summary p {
        margin-bottom: 10px;
        line-height: 1.5;
        font-size: 16px;
        color: #333;
        flex-grow: 1;
    }

    .document-date {
        color: #666;
        font-size: 14px;
        margin-top: auto;
    }

    /* Success Stories */
    .success-stories {
        padding: 60px 0;
        background-color: #2444CB;
    }

    .success-stories .section-title h2 {
        color: white;
        font-size: 32px;
    }

    .stories-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 30px;
    }

    .story-card {
        background-color: white;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    }

    .story-content {
        display: flex;
        flex-direction: column;
        height: 220px;
        background: url('../assets/light_bg.png');
        background-size: cover;
        background-position: center;
        padding: 25px;
    }

    .story-content h3 {
        padding: 0;
        font-size: 20px;
        color: #222222;
        height: 60px;
        margin: 0;
        display: flex;
        align-items: center;
    }

    .story-description {
        height: 160px;
    }

    .story-description p {
        font-size: 18px;
        color: #222222;
        line-height: 1.4;
        margin: 0;
    }

    /* Contact Section */
    .contact {
        padding: 60px 0;
        background-color: #022A4E;
    }
    .footer-links {
        display: flex;
        gap: 35px;
        margin-top: auto; /* 这将把链接推到容器底部 */
        padding-top: 30px;
    }

    .footer-link {
        color: #b3cde0;
        text-decoration: none;
        font-size: 14px;
        transition: color 0.3s ease;
    }

    .footer-link:hover {
        color: white;
    }

    /* 确保contact-info使用flex布局以支持底部对齐 */
    .contact-info {
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    .contact-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 50px;
    }

    .contact-info .section-title h2 {
        font-size: 44px;
        color: white;
    }

    .contact-info .section-title p {
        color: white;
        font-size: 16px;
        line-height: 1.5;
    }

    .contact-details {
        margin-top: 30px;
        color: white;
    }

    .contact-details p {
        margin-bottom: 10px;
    }

    .social-links {
        margin-top: 30px;
    }

    .social-icons {
        display: flex;
        gap: 20px;
    }

    .social-icons a img {
        height: 31px;
        width: 31px;
    }

    .contact-form {
        background-color: white;
        padding: 30px;
        border-radius: 8px;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
    }

    .contact-form h3 {
        color: #1a3a5f;
        margin-bottom: 20px;
        font-size: 24px;
    }

    .contact-form .form-group {
        margin-bottom: 20px;
        position: relative;
    }

    .contact-form input,
    .contact-form select,
    .contact-form textarea {
        width: 100%;
        padding: 12px 16px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 16px;
        box-sizing: border-box;
    }

    /* 为联系表单的下拉框也应用相同的样式 */
    .contact-form select {
        appearance: none;
        -webkit-appearance: none;
        -moz-appearance: none;
        background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
        background-repeat: no-repeat;
        background-position: calc(100% - 12px) center;
        background-size: 16px;
        padding-right: 40px;
    }

    .contact-form .btn-primary {
        background-color: #265FFF;
        width: 100%;
        color: white;
    }

    /* Report Modal */
    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1000;
    }

    .report-modal {
        background-color: white;
        padding: 30px;
        border-radius: 8px;
        max-width: 600px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
    }

    .report-content {
        margin: 20px 0;
        line-height: 1.6;
    }

    /* Analysis Recommendations New Section */
    .analysis-recommendations-new {
        padding: 60px 0;
        background-color: white;
    }

    .analysis-recommendations-new .section-title h2 {
        color: #222222;
        font-size: 32px;
        margin-bottom: 40px;
    }

    /* Search Mode Toggle */
    .search-mode-toggle {
        display: flex;
        justify-content: start;
        margin-bottom: 40px;
        gap: 20px;
    }

    .mode-btn {
        padding: 12px 24px;
        border: 2px solid #265FFF;
        background-color: white;
        color: #265FFF;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
    }

    .mode-btn.active {
        background-color: #265FFF;
        color: white;
    }

    .mode-btn:hover:not(.active) {
        background-color: #f0f5ff;
    }

    /* Search Mode Content */
    .search-mode-content {
        max-width: 1000px;
    }

    /* Search Box */
    .search-box {
        margin-bottom: 30px;
    }

    .search-input-wrapper {
        position: relative;
        max-width: 500px;
    }

    .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        width: 20px;
        height: 20px;
    }

    .search-input {
        width: 100%;
        padding: 12px 16px 12px 48px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 16px;
        box-sizing: border-box;
    }

    .search-input:focus {
        outline: none;
        border-color: #265FFF;
    }

    /* Industries and Countries Grid */
    .industries-grid,
    .countries-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 15px;
        margin-bottom: 30px;
        justify-content: start;
        /* 默认左对齐 */
    }

    /* 搜索结果和推荐国家的特殊样式 */
    .search-results-grid {
        justify-content: start;
        /* 确保左对齐 */
    }

    /* 当只有1个结果时 */
    .industries-grid.result-count-1,
    .countries-grid.result-count-1 {
        grid-template-columns: repeat(1, minmax(200px, max-content));
        max-width: max-content;
    }

    /* 当只有2个结果时 */
    .industries-grid.result-count-2,
    .countries-grid.result-count-2 {
        grid-template-columns: repeat(2, minmax(200px, 1fr));
        max-width: calc(2 * (200px + 15px));
    }

    /* 当只有3个结果时 */
    .industries-grid.result-count-3,
    .countries-grid.result-count-3 {
        grid-template-columns: repeat(3, minmax(200px, 1fr));
        max-width: calc(3 * (200px + 15px));
    }

    .industry-item,
    .country-item {
        padding: 15px 20px;
        border: 2px solid #265FFF;
        background-color: white;
        color: #265FFF;
        border-radius: 6px;
        text-align: center;
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        min-width: 200px;
        /* 确保最小宽度 */
        min-height: 54px; /* 固定高度，与Search按钮保持一致 */
        display: flex;
        align-items: center;
        justify-content: center;
        word-break: break-word; /* 允许单词换行 */
        line-height: 1.2; /* 调整行高 */
    }

    .industry-item.active,
    .country-item.active {
        background-color: #265FFF;
        color: white;
    }

    .industry-item:hover:not(.active),
    .country-item:hover:not(.active) {
        background-color: #f0f5ff;
    }

    /* Search Action - 增加上边距使按钮下移 */
    .search-action {
        margin-bottom: 40px;
        margin-top: 20px; /* 增加上边距，使Search按钮下移 */
    }

    .search-btn {
        padding: 12px 40px;
        background-color: #265FFF;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.3s ease;
        min-height: 54px; /* 与industry-item高度保持一致 */
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .search-btn:hover {
        background-color: #1a4fd8;
    }

    /* Recommended Countries */
    .recommended-countries {
        margin-top: 40px;
    }

    .recommendation-text {
        font-size: 16px;
        line-height: 1.6;
        color: #333;
        margin-bottom: 30px;
        max-width: 800px;
    }

    .recommendation-text strong {
        color: #265FFF;
    }

    .view-details-btn {
        margin-top: 30px;
    }

    .details-btn {
        padding: 12px 40px;
        background-color: #265FFF;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.3s ease;
    }

    .details-btn:hover {
        background-color: #1a4fd8;
    }

    /* Country Search Mode */
    .file-library-title {
        font-size: 20px;
        font-weight: 600;
        color: #222222;
        margin-bottom: 30px;
    }

    .country-search-form {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr auto;
        gap: 15px;
        margin-bottom: 40px;
        align-items: end;
    }

    .form-group {
        display: flex;
        flex-direction: column;
    }

    .form-select {
        padding: 12px 16px;
        border: 1px solid #ddd;
        border-radius: 6px;
        font-size: 16px;
        background-color: white;
        cursor: pointer;
    }

    .form-select:focus {
        outline: none;
        border-color: #265FFF;
    }

    /* 响应式设计 */
    @media (max-width: 1200px) {
        .container {
            padding: 0 40px;
        }

        /* 中等屏幕调整为两列 */
        .documents-grid {
            grid-template-columns: repeat(2, 1fr);
        }
    }

    @media (max-width: 992px) {
        .contact-container {
            grid-template-columns: 1fr;
            gap: 30px;
        }

        .diagnosis-form {
            grid-template-columns: 1fr;
        }

        .services-grid {
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        }

        .country-search-form {
            grid-template-columns: 1fr 1fr;
            gap: 15px;
        }
    }

    @media (max-width: 768px) {
        .container {
            padding: 0 20px;
        }

        .hero {
            padding: 40px 0;
        }

        .hero h1 {
            font-size: 28px;
        }

        .hero p {
            font-size: 14px;
        }

        .diagnosis,
        .analysis-recommend,
        .related-services,
        .featured-documents,
        .success-stories,
        .contact {
            padding: 40px 0;
        }

        .diagnosis .section-title h2 {
            font-size: 32px;
        }

        .contact-info .section-title h2 {
            font-size: 32px;
        }

        .analysis-grid {
            grid-template-columns: 1fr;
            gap: 20px;
        }

        /* 小屏幕调整为一列 */
        .documents-grid {
            grid-template-columns: 1fr;
        }

        .stories-grid {
            grid-template-columns: 1fr;
        }

        .form-buttons {
            flex-direction: column;
        }

        .report-buttons {
            padding: 0 15px;
        }

        .contact-form {
            padding: 20px;
        }

        .analysis-recommendations-new {
            padding: 40px 0;
        }

        .search-mode-toggle {
            flex-direction: column;
            gap: 10px;
        }

        .mode-btn {
            width: 100%;
        }

        .industries-grid,
        .countries-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 10px;
        }

        /* 移动端时，无论结果数量多少，都使用单列布局 */
        .industries-grid.result-count-1,
        .industries-grid.result-count-2,
        .industries-grid.result-count-3,
        .countries-grid.result-count-1,
        .countries-grid.result-count-2,
        .countries-grid.result-count-3 {
            grid-template-columns: 1fr;
            max-width: 100%;
        }

        .industry-item,
        .country-item {
            padding: 12px 15px;
            font-size: 14px;
            min-width: auto;
            /* 移动端取消最小宽度限制 */
            min-height: 48px; /* 移动端调整高度 */
        }

        .country-search-form {
            grid-template-columns: 1fr;
            gap: 15px;
        }

        .search-action {
            margin-top: 15px; /* 移动端调整上边距 */
        }

        .search-btn {
            min-height: 48px; /* 移动端调整高度 */
        }
    }

    @media (max-width: 480px) {
        .hero h1 {
            font-size: 24px;
        }

        .diagnosis .section-title h2 {
            font-size: 28px;
        }

        .contact-info .section-title h2 {
            font-size: 28px;
        }

        .social-icons {
            justify-content: center;
        }

        .analysis-recommendations-new .section-title h2 {
            font-size: 24px;
        }

        .search-input {
            font-size: 14px;
        }

        .recommendation-text {
            font-size: 14px;
        }
    }
    .image-text {
        /* 定位居中 */
        position: absolute;
        top: 35%;
        left: 50%;
        transform: translate(-50%, -50%);
        
        /* 强制显示（覆盖隐藏逻辑） */
        opacity: 1 !important;
        display: inline-block !important;
        visibility: visible !important;
        z-index: 999 !important;
        
        /* 英文换行关键样式 */
        width: 90%; /* 限制文字宽度（避免贴边），比容器窄10% */
        word-wrap: break-word; /* 强制长单词换行 */
        word-break: break-all; /* 连续字母/数字强制换行（针对超长篇幅） */
        white-space: normal; /* 取消 nowrap（确保换行生效） */
        
        /* 文字样式 */
        color: #fff;
        font-size: 30px;
        font-weight: 800;
        text-align: center;
        text-shadow: 0 2px 4px rgba(0,0,0,0.7);
        padding: 4px 0; /* 上下内边距，优化换行后间距 */
        pointer-events: none;
    }
</style>